// @angular
import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  Input,
  AfterViewInit,
  effect,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { formatNumber, NgClass } from "@angular/common";
// Own services
import { EntityControllerService } from "../../../../services/server/EntityController.service";
import { SessionDataService } from "../../../../services/shared/SessionDataService.service";
// Interfaces
import { Entity } from "../../../../interfaces/EntityGlobalInterface.type";
import { TranslateModule } from "@ngx-translate/core";

/**
 * Bar summary of an entity's project, license and activated device totals, fetched from the entity totals service.
 * Uses the `entity` input or the session entity, and reloads when the session entity changes.
 */
@Component({
  selector: "app-entity-total-snapshot",
  templateUrl: "./entity-total-snapshot.component.html",
  styleUrls: ["./entity-total-snapshot.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, TranslateModule]
})
export class EntityTotalSnapshotComponent implements OnInit, OnDestroy, AfterViewInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  currentEntity: Entity;
  entitySub: any;

  entityTotals: {
    activated: number;
    project: number;
    license: number;
    projectParsed: string;
    licenseParsed: string;
    activatedParsed: string;
  };
  @ViewChild("entityTotalsGraph") entityTotalsGraph: ElementRef;
  @Input() entity: Entity;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly EntityController: EntityControllerService,
    private readonly SessionDataService: SessionDataService
  ) {
    effect(() => {
      const entity = this.SessionDataService.entity();
      if (entity !== null) {
        this.currentEntity = entity;
        if (this.currentEntity && this.entityTotalsGraph) {
          this.getEntityTotals();
        }
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    if (!this.entity) {
      this.currentEntity = untracked(() => this.SessionDataService.entity());
    }
  }

  /***************************************************************************/
  // ANCHOR Post-render execution
  /***************************************************************************/

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.entity || this.currentEntity) {
        this.getEntityTotals();
      }
    }, 0);
  }

  /***************************************************************************/
  // ANCHOR Component destruction
  /***************************************************************************/

  ngOnDestroy(): void {}

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  setChart(): void {
    let max = Math.max(
      this.entityTotals.project,
      this.entityTotals.license,
      this.entityTotals.activated
    );
    this.entityTotalsGraph?.nativeElement.style.setProperty(
      "--entity-totals-graph-project",
      (this.entityTotals.project / max) * 100
    );
    this.entityTotalsGraph?.nativeElement.style.setProperty(
      "--entity-totals-graph-license",
      (this.entityTotals.license / max) * 100
    );
    this.entityTotalsGraph?.nativeElement.style.setProperty(
      "--entity-totals-graph-actives",
      (this.entityTotals.activated / max) * 100
    );
  }

  // Fetch totals per entity
  getEntityTotals(): void {
    this.EntityController.getEntityTotalDevices([
      this.entity ? this.entity.id : this.currentEntity.id
    ]).subscribe(response => {
      if (response["code"] === 0) {
        let entityTotals = response["body"][0];
        let numberFormat = untracked(() => this.SessionDataService.numberFormat());
        if (
          entityTotals?.project != null &&
          entityTotals?.license != null &&
          entityTotals?.activated != null
        ) {
          this.entityTotals = {
            project: entityTotals.project,
            license: entityTotals.license,
            activated: entityTotals.activated,
            projectParsed: formatNumber(entityTotals.project, numberFormat),
            licenseParsed: formatNumber(entityTotals.license, numberFormat),
            activatedParsed: formatNumber(entityTotals.activated, numberFormat)
          };
          this.setChart();
        } else {
          this.entityTotals = null;
        }
      }
    });
  }
}
